/**
 * ExercisePicker — full-screen modal for swapping the active exercise.
 *
 * Backed by GET /api/tools/gym/exercises. Three sections:
 *   1. Recents — most-recent 5 across all sessions
 *   2. All exercises (alphabetical, hidden excluded)
 *   3. Freeform — submits an arbitrary string; server-side falls back to
 *      resolveExerciseId → ad-hoc id, same path the voice tools use.
 *
 * Uses the native <dialog> element + showModal() for focus-trap and
 * backdrop click-to-dismiss. Safari supports this.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XIcon } from '@phosphor-icons/react';

interface ExerciseLite {
  id: string;
  name: string;
}

interface PickerResponse {
  exercises: ExerciseLite[];
  recents: ExerciseLite[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Receives a canonical id (from list/recents) OR an arbitrary string (freeform). */
  onPick: (idOrFreeform: string) => void;
}

export function ExercisePicker({ open, onClose, onPick }: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [data, setData] = useState<PickerResponse | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Open/close the native <dialog>.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setQuery('');
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  // Fetch once per open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await fetch('/api/tools/gym/exercises');
        if (!res.ok) return;
        const json = (await res.json()) as PickerResponse;
        if (!cancelled) setData(json);
      } catch {
        // ignore — list stays empty, freeform still works
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Native dialog: clicking the backdrop fires a click on the dialog
  // element itself (not a child) — dismiss on that.
  const onDialogClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const filtered = useMemo(() => {
    if (!data) return { recents: [] as ExerciseLite[], rest: [] as ExerciseLite[] };
    const q = query.trim().toLowerCase();
    const match = (e: ExerciseLite) =>
      !q || e.name.toLowerCase().includes(q) || e.id.toLowerCase().includes(q);
    const recentIds = new Set(data.recents.map((r) => r.id));
    return {
      recents: data.recents.filter(match),
      rest: data.exercises.filter((e) => !recentIds.has(e.id)).filter(match),
    };
  }, [data, query]);

  function handlePick(idOrFreeform: string) {
    const v = idOrFreeform.trim();
    if (!v) return;
    onPick(v);
    onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      className="exercise-picker"
      onClose={onClose}
      onClick={onDialogClick}
      onCancel={onClose}
    >
      <div className="exercise-picker-inner">
        <header className="exercise-picker-header">
          <input
            type="text"
            className="exercise-picker-search"
            placeholder="type or search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            // pressing Enter on a non-matching query submits as freeform
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const q = query.trim();
              if (!q) return;
              const hit =
                filtered.recents[0] ??
                filtered.rest[0] ??
                null;
              if (hit) handlePick(hit.id);
              else handlePick(q);
            }}
          />
          <button
            type="button"
            className="exercise-picker-close"
            onClick={onClose}
            aria-label="close"
          >
            <XIcon size={16} weight="regular" aria-hidden="true" />
          </button>
        </header>

        <div className="exercise-picker-body">
          {loading && <p className="exercise-picker-empty">loading…</p>}

          {filtered.recents.length > 0 && (
            <section className="exercise-picker-section">
              <h3 className="exercise-picker-section-title">recents</h3>
              <ul className="exercise-picker-list">
                {filtered.recents.map((e) => (
                  <li key={`r-${e.id}`}>
                    <button
                      type="button"
                      className="exercise-picker-item"
                      onClick={() => handlePick(e.id)}
                    >
                      {e.name}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {filtered.rest.length > 0 && (
            <section className="exercise-picker-section">
              <h3 className="exercise-picker-section-title">all exercises</h3>
              <ul className="exercise-picker-list">
                {filtered.rest.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      className="exercise-picker-item"
                      onClick={() => handlePick(e.id)}
                    >
                      {e.name}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!loading && filtered.recents.length === 0 && filtered.rest.length === 0 && (
            <p className="exercise-picker-empty">
              no match — press enter to log "{query.trim()}" as freeform
            </p>
          )}

          <section className="exercise-picker-section exercise-picker-freeform">
            <h3 className="exercise-picker-section-title">freeform</h3>
            <button
              type="button"
              className="exercise-picker-item exercise-picker-item-freeform"
              disabled={!query.trim()}
              onClick={() => handlePick(query.trim())}
            >
              use "{query.trim() || '…'}" as exercise name
            </button>
          </section>
        </div>
      </div>
    </dialog>
  );
}
